import React, { useEffect, useState } from 'react';
import { Customer, CustomerLocation } from '../../types';
import { customerService, CreateCustomerData } from '../../services/customerService';
import { settingService, Setting } from '../../services/settingService';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import { locationConversionCaption } from '../../utils/customerOrigin';
import { FiX } from 'react-icons/fi';
import Icon from '../common/Icon';

const ODISHA_STATE_VALUE = 'OD';

const defaultLocation = (): CustomerLocation => ({
  project_state: ODISHA_STATE_VALUE,
  project_district: '',
  project_location: '',
  company_sales_person: undefined,
});

function customerToForm(customer: Customer): CreateCustomerData {
  const locations =
    customer.locations && customer.locations.length > 0
      ? customer.locations.map((loc) => ({
          id: loc.id,
          customer_id: loc.customer_id,
          project_state: loc.project_state || '',
          project_district: loc.project_district || '',
          project_location: loc.project_location || '',
          company_sales_person: loc.company_sales_person ?? undefined,
          display_order: loc.display_order,
          converted_from_tender_id: loc.converted_from_tender_id ?? undefined,
          converted_from_lead_id: loc.converted_from_lead_id ?? undefined,
          converted_by: loc.converted_by ?? undefined,
          converted_by_name: loc.converted_by_name ?? undefined,
          sales_officer_id: loc.sales_officer_id ?? undefined,
          sales_officer_name: loc.sales_officer_name ?? undefined,
        }))
      : [defaultLocation()];
  return {
    customer_code: customer.customer_code || '',
    party_name: customer.party_name,
    contact_person: customer.contact_person || '',
    contact_no: customer.contact_no || '',
    secondary_contact_person: customer.secondary_contact_person || '',
    secondary_contact_no: customer.secondary_contact_no || '',
    email: customer.email || '',
    office_address: customer.office_address || '',
    status: customer.status,
    doj: customer.doj || '',
    last_bill_date: customer.last_bill_date || '',
    sales_officer_id: customer.sales_officer_id,
    brand_id: customer.brand_id,
    supplier_id: customer.supplier_id,
    work_order_value: customer.work_order_value,
    wo_allotment_date: customer.wo_allotment_date || '',
    tenure_months: customer.tenure_months,
    product_req: customer.product_req || '',
    qty_req: customer.qty_req || '',
    brand_in_use: customer.brand_in_use || '',
    locations,
  };
}

interface CustomerFormModalProps {
  isOpen: boolean;
  customerId: number;
  onClose: () => void;
  onSaved?: (customer: Customer) => void;
}

const CustomerFormModal: React.FC<CustomerFormModalProps> = ({ isOpen, customerId, onClose, onSaved }) => {
  const { user } = useAuth();
  const { users, brands, suppliers } = useAppData();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<CreateCustomerData>({
    party_name: '',
    status: 'inactive',
    locations: [defaultLocation()],
  });
  const [statuses, setStatuses] = useState<Setting[]>([]);
  const [companySalesOfficers, setCompanySalesOfficers] = useState<Setting[]>([]);
  const [projectStates, setProjectStates] = useState<Setting[]>([]);
  const [districtsByState, setDistrictsByState] = useState<Record<string, Setting[]>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !customerId) return;
    let cancelled = false;
    setError('');
    setLoading(true);
    Promise.all([
      customerService.getById(customerId),
      settingService.getByType('customer_status'),
      settingService.getByType('company_sales_officer'),
      settingService.getByType('project_state'),
    ])
      .then(([cust, statusList, officers, states]) => {
        if (cancelled) return;
        setCustomer(cust);
        setFormData(customerToForm(cust));
        setStatuses(statusList);
        setCompanySalesOfficers(officers);
        setProjectStates(states);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load customer');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId]);

  useEffect(() => {
    if (!isOpen) return;
    const locations = formData.locations || [];
    locations.forEach((loc) => {
      const stateKey = loc.project_state || '';
      if (!stateKey || districtsByState[stateKey]) return;
      settingService.getByType('project_district', false, stateKey).then((data) => {
        setDistrictsByState((prev) => (prev[stateKey] ? prev : { ...prev, [stateKey]: data }));
      }).catch(() => undefined);
    });
  }, [isOpen, formData.locations?.map((l) => l.project_state).join(',')]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;
    setSaving(true);
    setError('');
    try {
      const { last_bill_date: _omit, ...payload } = formData;
      const saved = await customerService.update(customer.id, payload);
      onSaved?.(saved);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70] p-2 sm:p-3">
      <div className="relative top-1 sm:top-6 mx-auto p-3 sm:p-4 border w-full max-w-4xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-2 sm:mb-3">
          <h3 className="text-sm sm:text-base font-bold text-gray-900">Edit Customer</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-0.5" aria-label="Close">
            <Icon component={FiX} className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading customer…</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">{error}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Party Name *</label>
                <input
                  type="text"
                  required
                  value={formData.party_name}
                  onChange={(e) => setFormData({ ...formData, party_name: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Customer Code</label>
                <input
                  type="text"
                  value={formData.customer_code || ''}
                  onChange={(e) => setFormData({ ...formData, customer_code: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Brand</label>
                <select
                  value={formData.brand_id || ''}
                  onChange={(e) => setFormData({ ...formData, brand_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                >
                  <option value="">Select Brand</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Supplier</label>
                <select
                  value={formData.supplier_id || ''}
                  onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Status</label>
                <select
                  value={formData.status || ''}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as CreateCustomerData['status'] })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                  required
                >
                  <option value="">Select Status</option>
                  {statuses.filter((s) => s.is_active).map((status) => (
                    <option key={status.id} value={status.setting_value}>
                      {status.display_name} {status.color_code && `(${status.color_code})`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person || ''}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Contact No</label>
                <input
                  type="tel"
                  value={formData.contact_no || ''}
                  onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Secondary Contact Person</label>
                <input
                  type="text"
                  value={formData.secondary_contact_person || ''}
                  onChange={(e) => setFormData({ ...formData, secondary_contact_person: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Secondary Contact No</label>
                <input
                  type="tel"
                  value={formData.secondary_contact_no || ''}
                  onChange={(e) => setFormData({ ...formData, secondary_contact_no: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Sales Officer</label>
                <select
                  value={formData.sales_officer_id || ''}
                  onChange={(e) => setFormData({ ...formData, sales_officer_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={user?.role === 'executive'}
                >
                  <option value="">Select Sales Officer</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">DOJ</label>
                <input
                  type="date"
                  value={formData.doj || ''}
                  onChange={(e) => setFormData({ ...formData, doj: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Last Order Date</label>
                <p className="block w-full border border-gray-200 bg-gray-50 rounded px-2 py-1 sm:py-1.5 text-sm text-gray-700">
                  {customer?.last_bill_date ? formatDate(customer.last_bill_date) : '-'}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">Set automatically from the latest order</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Work Order Value</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.work_order_value || ''}
                  onChange={(e) => setFormData({ ...formData, work_order_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">WO Allotment Date</label>
                <input
                  type="date"
                  value={formData.wo_allotment_date || ''}
                  onChange={(e) => setFormData({ ...formData, wo_allotment_date: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Tenure (Months)</label>
                <input
                  type="number"
                  value={formData.tenure_months || ''}
                  onChange={(e) => setFormData({ ...formData, tenure_months: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Product Req</label>
                <input
                  type="text"
                  value={formData.product_req || ''}
                  onChange={(e) => setFormData({ ...formData, product_req: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Qty Req</label>
                <input
                  type="text"
                  value={formData.qty_req || ''}
                  onChange={(e) => setFormData({ ...formData, qty_req: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Brand in Use</label>
                <input
                  type="text"
                  value={formData.brand_in_use || ''}
                  onChange={(e) => setFormData({ ...formData, brand_in_use: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                />
              </div>
              <div className="col-span-1 sm:col-span-2 lg:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Office Address</label>
                <textarea
                  value={formData.office_address || ''}
                  onChange={(e) => setFormData({ ...formData, office_address: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                  rows={1}
                />
              </div>
              {(formData.locations || []).map((loc, idx) => (
                <React.Fragment key={idx}>
                  <div className="col-span-1 sm:col-span-2 lg:col-span-4 text-xs font-bold text-gray-600 mt-1 bg-gray-100 p-[7px] flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span>Location {idx + 1}</span>
                    <span className="font-medium text-gray-500">{locationConversionCaption(loc)}</span>
                    {(formData.locations?.length ?? 0) > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            locations: formData.locations!.filter((_, i) => i !== idx),
                          })
                        }
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Project State</label>
                    <select
                      value={loc.project_state || ''}
                      onChange={(e) => {
                        const next = [...(formData.locations || [])];
                        next[idx] = { ...next[idx], project_state: e.target.value, project_district: '' };
                        setFormData({ ...formData, locations: next });
                      }}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select State</option>
                      {projectStates.map((state) => (
                        <option key={state.id} value={state.setting_value}>{state.display_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Project District</label>
                    <select
                      value={loc.project_district || ''}
                      onChange={(e) => {
                        const next = [...(formData.locations || [])];
                        next[idx] = { ...next[idx], project_district: e.target.value };
                        setFormData({ ...formData, locations: next });
                      }}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                      disabled={!loc.project_state}
                    >
                      <option value="">{loc.project_state ? 'Select District' : 'Select State First'}</option>
                      {(districtsByState[loc.project_state || ''] || []).map((district) => (
                        <option key={district.id} value={district.setting_value}>{district.display_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Project Location</label>
                    <input
                      type="text"
                      value={loc.project_location || ''}
                      onChange={(e) => {
                        const next = [...(formData.locations || [])];
                        next[idx] = { ...next[idx], project_location: e.target.value };
                        setFormData({ ...formData, locations: next });
                      }}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Company Sales Officer</label>
                    <select
                      value={loc.company_sales_person ?? ''}
                      onChange={(e) => {
                        const next = [...(formData.locations || [])];
                        const val = e.target.value;
                        next[idx] = { ...next[idx], company_sales_person: val === '' ? undefined : Number(val) };
                        setFormData({ ...formData, locations: next });
                      }}
                      className="block w-full border border-gray-300 rounded px-2 py-1 sm:py-1.5 text-sm"
                    >
                      <option value="">Select Company Sales Officer</option>
                      {companySalesOfficers.map((officer) => (
                        <option key={officer.id} value={officer.id}>{officer.display_name}</option>
                      ))}
                    </select>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="flex flex-row flex-wrap justify-between items-center gap-2 pt-2 sm:pt-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, locations: [...(formData.locations || []), defaultLocation()] })}
                className="text-sm text-yellow-600 hover:text-yellow-800 font-medium"
              >
                + Add location
              </button>
              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1 sm:py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-3 py-1 sm:py-1.5 bg-yellow-500 text-black-950 rounded hover:bg-yellow-600 text-sm font-medium font-bold disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Update'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerFormModal;
